import React, { useState, useMemo, useEffect } from 'react';
import { PartnerApplication } from '../types';
import { Clock, Building, Mail, Calendar, Trash2 } from 'lucide-react';

interface ApplicationQueueListProps {
  applications: Array<{
    partner: any;
    documents: any[];
    offers_count: number;
  }>;
  onSelectApplication: (application: any) => void;
  onPreApprove: (partnerId: string) => void;
  onReject: (partnerId: string, reason: string) => void;
  onDelete: (partnerId: string) => void;
  loading: boolean;
  selectedApplication: any;
}

// Add this block - Define WORKFLOW_STAGE_TABS constant
const WORKFLOW_STAGE_TABS = [
  { key: 'application', label: 'Application Review', icon: '📋', color: 'yellow' },
  { key: 'pre_approved', label: 'Pre-Approved', icon: '✅', color: 'blue' },
  { key: 'scheduling_call', label: 'Scheduling & Call', icon: '📞', color: 'purple' },
  { key: 'contract', label: 'Contract Process', icon: '📝', color: 'indigo' },
  { key: 'onboarded', label: 'Account Creation', icon: '🎉', color: 'green' }
];

const ApplicationQueueList: React.FC<ApplicationQueueListProps> = ({
  applications,
  onSelectApplication,
  onPreApprove,
  onReject,
  onDelete,
  loading,
  selectedApplication
}) => {
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // NEW: Max offers input for pre-approval
  const [maxOffersAllowed, setMaxOffersAllowed] = useState(5);
  
  // Add this block - Missing state declarations
  const [activeTab, setActiveTab] = useState('application');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const selectedApplicationId = selectedApplication?.partner?.id || null;

  // Helper function to get the timestamp of the most recent status change
  const getLastStatusChangeTimestamp = (partner: any): number => {
    const timestamps: number[] = [];
    
    // Add relevant timestamps based on current status
    if (partner.application_date) {
      timestamps.push(new Date(partner.application_date).getTime());
    }
    
    if (partner.approved_date) {
      timestamps.push(new Date(partner.approved_date).getTime());
    }
    
    if (partner.updated_at) {
      timestamps.push(new Date(partner.updated_at).getTime());
    }
    
    // Return the most recent timestamp, or application date as fallback
    return timestamps.length > 0 ? Math.max(...timestamps) : new Date(partner.application_date).getTime();
  };

  // Helper function to determine the current workflow stage of an application
  const getWorkflowStage = (partner: any): string => {
    // Onboarded - has credentials sent, active status, OR contract signed
    // If contract is signed, the partner moves to Account Creation stage
    if (partner.credentials_sent_at || partner.status === 'active' || partner.contract_signed_at) {
      return 'onboarded';
    }
    
    // Contract Process - contract sent or conditions added (but NOT signed yet)
    // Once contract is signed, application moves to Account Creation (onboarded)
    if (partner.contract_sent_at || partner.contract_conditions_added) {
      return 'contract';
    }
    
    // Scheduling & Call - calendar link sent or call completed
    if (partner.onboarding_call_completed || partner.calendar_link_sent_at || 
        partner.calendar_slot_selected || partner.selected_time_slot) {
      return 'scheduling_call';
    }
    
    // Pre-Approved - explicitly pre-approved status
    if (partner.status === 'pre_approved' || partner.approved_date) {
      return 'pre_approved';
    }
    
    // Application Review - default for new applications
    return 'application';
  };

  // Group and sort applications by workflow stage
  const groupedApplications = useMemo(() => {
    const grouped: Record<string, PartnerApplication[]> = {
      application: [],
      pre_approved: [],
      scheduling_call: [],
      contract: [],
      onboarded: []
    };

    // Group applications by workflow stage
    applications.forEach((app) => {
      const stage = getWorkflowStage(app.partner);
      grouped[stage].push(app);
    });

    // Sort each group by oldest unchanged status first (oldest timestamp = most pressing)
    Object.keys(grouped).forEach((stage) => {
      grouped[stage as string].sort((a, b) => {
        const timestampA = getLastStatusChangeTimestamp(a.partner);
        const timestampB = getLastStatusChangeTimestamp(b.partner);
        return timestampA - timestampB; // Ascending order (oldest first)
      });
    });

    return grouped;
  }, [applications]);

  // CRITICAL FIX: Auto-switch to first non-empty tab - moved AFTER groupedApplications computation
  useEffect(() => {
    // Check if current tab has applications
    const currentTabApps = groupedApplications[activeTab];
    
    // If current tab is empty, find first non-empty tab
    if (currentTabApps.length === 0) {
      const firstNonEmptyTab = WORKFLOW_STAGE_TABS.find(
        tab => groupedApplications[tab.key].length > 0
      );
      
      if (firstNonEmptyTab) {
        setActiveTab(firstNonEmptyTab.key);
      }
    }
  }, [applications, activeTab]); // REMOVED groupedApplications from dependencies

  // Get applications for the active tab
  const displayedApplications = groupedApplications[activeTab];

  // Helper function to format date as "MMM DD, YYYY"
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper function to get status badge styling
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Review' };
      case 'pre_approved':
        return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Pre-Approved' };
      case 'active':
        return { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' };
      case 'suspended':
        return { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Suspended' };
      case 'rejected':
        return { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    }
  };

  // Helper function to get workflow progress percentage
  const getWorkflowProgress = (partner: any): { percentage: number; stage: string } => {
    // Define workflow stages based on actual database columns
    const stages = [
      { 
        key: 'application_date', 
        label: 'Application Submitted',
        check: (p: any) => !!p.application_date 
      },
      { 
        key: 'pre_approved', 
        label: 'Pre-Approved',
        check: (p: any) => p.status === 'pre_approved' || p.status === 'active'
      },
      { 
        key: 'calendar_scheduled', 
        label: 'Calendar Scheduling',
        check: (p: any) => p.calendar_link_sent_at || p.calendar_slot_selected || p.selected_time_slot
      },
      { 
        key: 'call_completed', 
        label: 'Onboarding Call Completed',
        check: (p: any) => p.onboarding_call_completed
      },
      { 
        key: 'contract_conditions', 
        label: 'Contract Conditions Added',
        check: (p: any) => p.contract_conditions_added
      },
      { 
        key: 'contract_sent', 
        label: 'Contract Sent',
        check: (p: any) => p.contract_sent_at
      },
      { 
        key: 'contract_signed', 
        label: 'Contract Signed',
        check: (p: any) => p.contract_signed_at
      },
      { 
        key: 'fully_onboarded', 
        label: 'Fully Onboarded',
        check: (p: any) => p.credentials_sent_at || p.status === 'active'
      }
    ];

    let completedStages = 0;
    let currentStage = 'Application Submitted';

    stages.forEach((stage, index) => {
      if (stage.check(partner)) {
        completedStages = index + 1;
        currentStage = stage.label;
      }
    });

    // Calculate exact percentage based on completed stages
    const percentage = Math.round((completedStages / stages.length) * 100);

    return {
      percentage,
      stage: currentStage
    };
  };

  const handleDeleteClick = (e: React.MouseEvent, application: PartnerApplication) => {
    e.stopPropagation(); // Prevent selecting the application when clicking delete
    
    // PROTECTION: Check if application is in Account Creation stage
    const isInAccountCreation = getWorkflowStage(application.partner) === 'onboarded';
    
    if (isInAccountCreation) {
      alert(
        'Cannot delete application in Account Creation stage.\n\n' +
        `Business: ${application.partner.business_name}\n\n` +
        'Applications that have reached the account creation stage cannot be deleted. ' +
        'The partner onboarding process is nearly complete.'
      );
      return;
    }
    
    const confirmed = window.confirm(
      `Are you sure you want to delete this application?\n\n` +
      `Business: ${application.partner.business_name}\n` +
      `Email: ${application.partner.contact_email}\n\n` +
      `This action cannot be undone and will permanently remove:\n` +
      `• Partner application\n` +
      `• All associated offers\n` +
      `• Partner profile data`
    );

    if (confirmed) {
      setDeletingId(application.partner.id);
      
      // Fix: Use onDelete from props instead of onDeleteApplication
      onDelete(application.partner.id);
      
      // Reset deleting state after a short delay to allow for the delete operation
      setTimeout(() => {
        setDeletingId(null);
      }, 2000);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications</h3>
        <p className="text-sm text-gray-500">No partner applications found</p>
      </div>
    );
  }

  // Get tab color classes with updated color palette
  const getTabColorClasses = (color: string, isActive: boolean) => {
    const colors = {
      yellow: {
        active: 'bg-yellow-100 text-yellow-800 border-yellow-500',
        inactive: 'bg-white text-yellow-600 border-gray-200 hover:bg-yellow-50'
      },
      blue: {
        active: 'bg-blue-100 text-blue-800 border-blue-500',
        inactive: 'bg-white text-blue-600 border-gray-200 hover:bg-blue-50'
      },
      purple: {
        active: 'bg-purple-100 text-purple-800 border-purple-500',
        inactive: 'bg-white text-purple-600 border-gray-200 hover:bg-purple-50'
      },
      indigo: {
        active: 'bg-indigo-100 text-indigo-800 border-indigo-500',
        inactive: 'bg-white text-indigo-600 border-gray-200 hover:bg-indigo-50'
      },
      green: {
        active: 'bg-green-100 text-green-800 border-green-500',
        inactive: 'bg-white text-green-600 border-gray-200 hover:bg-green-50'
      }
    };
    
    return colors[color as keyof typeof colors][isActive ? 'active' : 'inactive'];
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <h2 className="text-xl font-bold text-white">Application Queue</h2>
        <p className="text-blue-100 text-sm mt-1">{applications.length} total applications</p>
      </div>

      {/* Workflow Stage Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex overflow-x-auto">
          {WORKFLOW_STAGE_TABS.map((tab) => {
            const count = groupedApplications[tab.key].length;
            const isActive = activeTab === tab.key;
            
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 min-w-[160px] px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  getTabColorClasses(tab.color, isActive)
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{tab.icon}</span>
                  <span className="truncate">{tab.label}</span>
                  <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                    isActive ? 'bg-white bg-opacity-50' : 'bg-gray-100'
                  }`}>
                    {count}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Applications List for Active Tab */}
      {displayedApplications.length === 0 ? (
        <div className="p-8 text-center">
          <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900 mb-1">
            No {WORKFLOW_STAGE_TABS.find(t => t.key === activeTab)?.label} Applications
          </h3>
          <p className="text-sm text-gray-500">
            {activeTab === 'application' && 'All applications have been reviewed'}
            {activeTab === 'pre_approved' && 'No pre-approved applications waiting for next steps'}
            {activeTab === 'scheduling_call' && 'No applications in scheduling or call stage'}
            {activeTab === 'contract' && 'No applications in contract process'}
            {activeTab === 'onboarded' && 'No fully onboarded partners yet'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
          {displayedApplications.map((application, index) => {
            const { partner } = application;
            const isSelected = selectedApplicationId === partner.id;
            const isDeleting = deletingId === partner.id;
            const workflowStage = getWorkflowStage(partner);
            const isInAccountCreation = workflowStage === 'onboarded';
            const daysSinceLastChange = Math.floor(
              (new Date().getTime() - getLastStatusChangeTimestamp(partner)) / (1000 * 60 * 60 * 24)
            );
            const formattedDate = formatDate(partner.application_date);
            const statusBadge = getStatusBadge(partner.status);
            const workflowProgress = getWorkflowProgress(partner);

            return (
              <div
                key={partner.id}
                onClick={() => !isDeleting && onSelectApplication(application)}
                className={`p-4 cursor-pointer transition-colors relative ${
                  isSelected
                    ? 'bg-blue-50 border-l-4 border-blue-600' :'hover:bg-gray-50 border-l-4 border-transparent'
                } ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {/* Priority Badge (shows position in queue) */}
                <div className="absolute top-2 left-2 bg-gray-700 text-white text-xs font-bold px-2 py-1 rounded">
                  #{index + 1}
                </div>

                {/* Delete Button - Hidden in Account Creation stage */}
                {!isInAccountCreation && (
                  <button
                    onClick={(e) => handleDeleteClick(e, application)}
                    disabled={isDeleting}
                    className="absolute top-2 right-2 p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors z-10 disabled:opacity-50"
                    title="Delete Application"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                
                {/* Account Creation Protection Badge - Moved below to not cover date */}
                {isInAccountCreation && (
                  <div className="absolute top-12 right-2 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded shadow-sm">
                    Protected
                  </div>
                )}

                <div className="flex items-start justify-between pr-8 pl-10">
                  <div className="flex-1 min-w-0">
                    {/* Business Name */}
                    <div className="flex items-center mb-2">
                      <Building className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <h3 className={`text-sm font-semibold truncate ${
                        isSelected ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {partner.business_name}
                      </h3>
                    </div>

                    {/* Contact Email */}
                    <div className="flex items-center mb-2">
                      <Mail className="w-3 h-3 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-xs text-gray-600 truncate">{partner.contact_email}</span>
                    </div>

                    {/* Time in Current Status */}
                    <div className="flex items-center mb-2">
                      <Calendar className="w-3 h-3 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-xs text-gray-500">
                        {daysSinceLastChange === 0 ? 'Updated today' : 
                         `${daysSinceLastChange} ${daysSinceLastChange === 1 ? 'day' : 'days'} in ${statusBadge.label.toLowerCase()}`}
                      </span>
                    </div>

                    {/* Workflow Progress Bar */}
                    <div className="mt-3 mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600">Workflow Progress</span>
                        <span className="text-xs font-medium text-gray-900">{workflowProgress.percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            partner.status === 'active' ? 'bg-green-600' :
                            partner.status === 'pre_approved' ? 'bg-blue-600' : 'bg-yellow-600'
                          }`}
                          style={{ width: `${workflowProgress.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 mt-1 block">{workflowProgress.stage}</span>
                    </div>
                  </div>

                  {/* Status Badge with Urgency Indicator */}
                  <div className="ml-3 flex-shrink-0 flex flex-col items-end gap-1">
                    {/* Urgency Badge based on days in status */}
                    {daysSinceLastChange > 0 && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        daysSinceLastChange > 7
                          ? 'bg-red-100 text-red-800'
                          : daysSinceLastChange > 3
                          ? 'bg-orange-100 text-orange-800' :'bg-green-100 text-green-800'
                      }`}>
                        {daysSinceLastChange > 7 ? '🔥 Urgent' : 
                         daysSinceLastChange > 3 ? '⚠️ Attention': '✓ Recent'}
                      </span>
                    )}
                    
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      Applied {formattedDate}
                    </span>
                  </div>
                </div>

                {/* Category Badge */}
                {partner.business_type && (
                  <div className="mt-2 pl-10">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {partner.business_type}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApplicationQueueList;